import { useEffect, useState } from "react";
import axios from "axios";
import { Barbell, ArrowRight, DownloadSimple, Warning, CheckCircle, ShareNetwork, Image as ImageIcon } from "@phosphor-icons/react";
import { getUserId } from "@/lib/userId";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── helper: update or insert a <meta> tag by name/property ────────────
function setMeta(key, value, attr = "property") {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

/**
 * SharedTemplate — public landing for shareable workout templates.
 *
 * Routes: /t/<id>
 *
 * No onboarding gate. Anyone with the link can view the template (exercise list,
 * sets/reps, category). The "Save to my UpTop" button calls the user-scoped
 * import endpoint:
 *   - If the visitor is an existing UpTop user (X-User-Id in localStorage),
 *     it imports immediately and routes them to / with the new id flagged.
 *   - If they're brand-new, the same call still works — `getUserId()` mints a
 *     device:UUID on the fly — and after onboarding they'll find the imported
 *     template waiting in their library.
 *
 * This is the viral surface area: shareable links → reddit/twitter/IG bio.
 */
export default function SharedTemplate({ templateId }) {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [importing, setImporting] = useState(false);
  const [imported, setImported] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/templates/share/${templateId}`);
        if (!cancelled) {
          setTemplate(r.data);
          // ── Dynamically update document title + Open Graph meta tags so
          // modern crawlers that execute JavaScript (Discord, Slack newer
          // versions, LinkedIn) read the per-template card instead of the
          // generic UpTop fallback baked into index.html.
          const ogUrl = `${API}/templates/share/${templateId}/og.png`;
          const pageUrl = window.location.href;
          const title = `${r.data.name} — Workout on UpTop`;
          const desc = r.data.description
            ? r.data.description.slice(0, 200)
            : `${r.data.exercise_count} exercises · ${r.data.set_count} sets · ${r.data.category}. Tap to save to your UpTop.`;
          document.title = title;
          setMeta("description", desc, "name");
          setMeta("og:title", title);
          setMeta("og:description", desc);
          setMeta("og:image", ogUrl);
          setMeta("og:url", pageUrl);
          setMeta("og:type", "article");
          setMeta("twitter:title", title, "name");
          setMeta("twitter:description", desc, "name");
          setMeta("twitter:image", ogUrl, "name");
        }
      } catch (e) {
        if (!cancelled) {
          if (e?.response?.status === 404) {
            setError("This template link doesn't exist or was deleted.");
          } else {
            setError("Couldn't load template. Try again in a moment.");
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [templateId]);

  const handleDownloadCard = async () => {
    try {
      const url = `${API}/templates/share/${templateId}/og.png`;
      const res = await fetch(url);
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = `uptop-${(template?.name || "workout").toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}.png`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
    } catch (e) {
      /* ignore */
    }
  };

  const handleSave = async () => {
    setImporting(true);
    try {
      // getUserId() ensures the visitor has a stable device:UUID before we
      // import; the request is sent with that header by the existing
      // global axios interceptor in App.js — but on this public page App.js
      // is NOT mounted, so we attach it manually.
      const userId = getUserId();
      await axios.post(`${API}/templates/import/${templateId}`, {}, {
        headers: { "X-User-Id": userId },
      });
      // Stash a pointer so the main App can highlight the new template on first
      // boot after this visitor opens the app.
      try {
        localStorage.setItem(
          "uptop-imported-template",
          JSON.stringify({ source_id: templateId, name: template?.name, at: Date.now() }),
        );
      } catch (e) { /* ignore */ }
      setImported(true);
    } catch (e) {
      setError("Couldn't save template. Try again in a moment.");
    } finally {
      setImporting(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (e) { /* ignore */ }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-[#E05A1D] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !template) {
    return (
      <div className="min-h-screen bg-[#0F0F0F] text-white flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center">
          <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center border border-red-500/40 bg-red-500/10 text-red-400">
            <Warning size={24} weight="bold" />
          </div>
          <h1
            className="text-3xl font-bold uppercase tracking-tight text-white mb-2"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            <span className="text-[#E05A1D]">UP</span>TOP
          </h1>
          <p className="text-sm text-[#A1A1AA] mb-6">{error || "Template not found."}</p>
          <a
            href="/"
            className="inline-flex items-center gap-2 bg-[#E05A1D] hover:bg-[#F06B2E] text-white font-bold uppercase tracking-wide px-5 py-3 text-xs transition-colors"
            data-testid="shared-template-back-link"
          >
            Open UpTop
            <ArrowRight size={14} weight="bold" />
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F0F0F] text-white" data-testid="shared-template-page">
      <div className="max-w-2xl mx-auto px-5 py-10 sm:py-16">
        {/* Brand */}
        <a
          href="/"
          className="inline-block mb-8"
          data-testid="shared-template-brand"
        >
          <h1
            className="text-3xl font-black uppercase tracking-tight"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            <span className="text-[#E05A1D]">UP</span>TOP
          </h1>
        </a>

        {/* Pill */}
        <div className="inline-flex items-center gap-2 border border-[#E05A1D]/40 bg-[#E05A1D]/10 text-[#E05A1D] px-3 py-1 mb-4">
          <ShareNetwork size={12} weight="bold" />
          <span className="text-[10px] font-bold uppercase tracking-[0.2em]">
            Shared workout
          </span>
        </div>

        {/* Title */}
        <h2
          className="text-4xl sm:text-5xl font-black uppercase tracking-tighter leading-[0.95] mb-4"
          style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          data-testid="shared-template-name"
        >
          {template.name}
        </h2>

        {/* Meta */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-[#A1A1AA] mb-8">
          <span className="font-semibold uppercase tracking-wider text-[#E05A1D]">
            {template.category}
          </span>
          <span>·</span>
          <span>{template.equipment}</span>
          <span>·</span>
          <span>
            <strong className="text-white">{template.exercise_count}</strong> exercises
          </span>
          <span>·</span>
          <span>
            <strong className="text-white">{template.set_count}</strong> sets
          </span>
        </div>

        {template.description && (
          <p className="text-sm text-[#A1A1AA] leading-relaxed mb-8 border-l-2 border-[#E05A1D]/40 pl-4">
            {template.description}
          </p>
        )}

        {/* Exercise list */}
        <div className="border border-[#27272A] bg-[#161616] mb-8" data-testid="shared-template-exercises">
          {(template.exercises || []).map((ex, i) => (
            <div
              key={i}
              className={`p-4 ${i > 0 ? "border-t border-[#27272A]" : ""}`}
            >
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-[#0F0F0F] border border-[#27272A] shrink-0">
                  <Barbell size={14} weight="bold" className="text-[#E05A1D]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3
                    className="text-base font-bold uppercase tracking-tight text-white truncate"
                    style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                  >
                    {ex.name}
                  </h3>
                  {(ex.sets || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {ex.sets.map((s, j) => (
                        <span
                          key={j}
                          className="text-[11px] font-mono bg-[#0F0F0F] border border-[#27272A] px-2 py-1 text-[#A1A1AA]"
                        >
                          {s.reps}×{s.weight && s.weight > 0 ? `${s.weight}kg` : "BW"}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTAs */}
        {imported ? (
          <div
            className="border border-[#22c55e]/40 bg-[#22c55e]/10 p-5 flex items-start gap-3"
            data-testid="shared-template-saved"
          >
            <div className="w-9 h-9 flex items-center justify-center bg-[#22c55e]/20 text-[#22c55e] shrink-0">
              <CheckCircle size={18} weight="fill" />
            </div>
            <div className="min-w-0 flex-1">
              <p
                className="text-base font-bold uppercase tracking-tight text-white"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                Saved to your UpTop
              </p>
              <p className="text-xs text-[#A1A1AA] mt-1 mb-3">
                Open UpTop and find <strong className="text-white">{template.name}</strong> in your templates.
              </p>
              <a
                href="/"
                className="inline-flex items-center gap-2 bg-[#E05A1D] hover:bg-[#F06B2E] text-white font-bold uppercase tracking-wide px-4 py-2.5 text-xs transition-colors"
                data-testid="shared-template-open-app"
              >
                Open UpTop
                <ArrowRight size={14} weight="bold" />
              </a>
            </div>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleSave}
              disabled={importing}
              className="flex-1 bg-[#E05A1D] hover:bg-[#F06B2E] disabled:opacity-50 text-white font-bold uppercase tracking-wide px-5 py-3.5 text-sm flex items-center justify-center gap-2 transition-colors active:scale-[0.98]"
              data-testid="shared-template-save-btn"
            >
              {importing ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <DownloadSimple size={16} weight="bold" />
                  Save to my UpTop
                </>
              )}
            </button>
            <button
              onClick={handleCopyLink}
              className="border border-[#27272A] hover:border-[#3f3f46] text-[#A1A1AA] hover:text-white font-bold uppercase tracking-wide px-5 py-3.5 text-xs flex items-center justify-center gap-2 transition-colors"
              data-testid="shared-template-copy-link"
            >
              {copied ? "Link copied!" : "Copy link"}
            </button>
          </div>
        )}

        {/* Always-visible secondary action: download the OG share card so users
            can post it manually on Reddit/IG/Twitter where auto-unfurl might
            not pick up dynamic tags. */}
        <button
          onClick={handleDownloadCard}
          className="mt-3 w-full border border-[#27272A] hover:border-[#3f3f46] text-[#71717A] hover:text-white font-bold uppercase tracking-wide px-5 py-3 text-[11px] flex items-center justify-center gap-2 transition-colors"
          data-testid="shared-template-download-card"
        >
          <ImageIcon size={14} weight="bold" />
          Download share image
        </button>

        {/* Footer */}
        <p className="mt-10 text-xs text-[#71717A] text-center">
          UpTop — track your lifts. Watch streaks. Crush PRs.
        </p>
      </div>
    </div>
  );
}
